import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Divider,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Stack,
  Button,
} from '@mui/material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { BarChart } from '@mui/x-charts/BarChart';
import { MaterialReactTable } from 'material-react-table';
import axios from 'axios';
import API_URL from '../../../config/api';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  DirectionsCar as CarIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
} from '@mui/icons-material';

function getToken() {
  return localStorage.getItem('token');
}

function formatBRL(value) {
  return (value ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function exibirPlaca(placa) {
  const raw = placa?.toUpperCase().replace(/[^A-Z0-9]/g, '') || '';
  if (raw.length === 7) {
    const isMercosul = /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/.test(raw);
    return isMercosul
      ? raw
      : `${raw.slice(0, 3)}-${raw.slice(3)}`;
  }
  return raw;
}

export default function AnaliseVeiculos() {
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState([]);
  const [maisAlugados, setMaisAlugados] = useState([]);
  const [veiculoMaisLucro, setVeiculoMaisLucro] = useState(null);
  const [veiculoMenosLucro, setVeiculoMenosLucro] = useState(null);
  const [estatisticas, setEstatisticas] = useState({
    totalVeiculos: 0,
    receitaTotal: 0,
    lucroTotal: 0,
    taxaOcupacaoMedia: 0,
  });

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const config = {
          headers: { Authorization: `Bearer ${getToken()}` }
        };

        const response = await axios.get(`${API_URL}/relatorios/analise-veiculos`, config);
        const veiculos = response.data.map(v => ({
          ...v,
          nomeVeiculo: `${exibirPlaca(v.placa)} - ${v.modelo}/${v.marca}`,
          nomeCompleto: `${exibirPlaca(v.placa)} - ${v.modelo}/${v.marca} (${v.ano})`,
        }));

        setDados(veiculos);
        setMaisAlugados(veiculos);

        // Calcular estatísticas
        const veiculosComAlugueis = veiculos.filter(v => v.totalAlugueis > 0);
        const stats = {
          totalVeiculos: veiculos.length,
          receitaTotal: veiculos.reduce((sum, v) => sum + parseFloat(v.receitaLiquida || 0), 0),
          lucroTotal: veiculos.reduce((sum, v) => sum + parseFloat(v.lucroTotal || 0), 0),
          taxaOcupacaoMedia: veiculosComAlugueis.length > 0 
            ? veiculosComAlugueis.reduce((sum, v) => sum + parseFloat(v.taxaOcupacao || 0), 0) / veiculosComAlugueis.length 
            : 0,
        };
        setEstatisticas(stats);

        // Veículo com maior e menor lucro
        if (veiculosComAlugueis.length > 0) {
          const maxLucro = veiculosComAlugueis.reduce((max, v) => 
            parseFloat(v.receitaLiquida) > parseFloat(max.receitaLiquida) ? v : max);
          const minLucro = veiculosComAlugueis.reduce((min, v) => 
            parseFloat(v.receitaLiquida) < parseFloat(min.receitaLiquida) ? v : min);
          
          setVeiculoMaisLucro(maxLucro);
          setVeiculoMenosLucro(minLucro);
        }
      } catch (err) {
        console.error('Erro ao carregar análise:', err);
        setDados([]);
        setMaisAlugados([]);
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  const exportarPDF = () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const dataAtual = new Date().toLocaleString('pt-BR');

    // ===== CABEÇALHO =====
    let yPos = 15;
    
    doc.setFillColor(31, 31, 31);
    doc.rect(0, 0, pageWidth, 25, 'F');
    
    doc.setTextColor(247, 139, 0);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('DELTA RENT CAR', pageWidth / 2, 12, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text('ANÁLISE DETALHADA DE VEÍCULOS', pageWidth / 2, 20, { align: 'center' });

    yPos = 32;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Gerado em: ${dataAtual}`, pageWidth - 15, yPos, { align: 'right' });

    yPos = 40;

    // ===== RESUMO ESTATÍSTICO =====
    doc.setFillColor(247, 139, 0);
    doc.rect(15, yPos - 5, pageWidth - 30, 8, 'F');
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('RESUMO ESTATÍSTICO', pageWidth / 2, yPos, { align: 'center' });

    yPos += 10;
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');

    // Cards em colunas
    const col1X = 15;
    const col2X = pageWidth / 2 + 10;
    let col1Y = yPos;
    let col2Y = yPos;

    // Coluna 1
    doc.setFont('helvetica', 'bold');
    doc.text('Total de Veículos:', col1X, col1Y);
    doc.setFont('helvetica', 'normal');
    doc.text(estatisticas.totalVeiculos.toString(), col1X + 40, col1Y);
    col1Y += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Receita Total:', col1X, col1Y);
    doc.setFont('helvetica', 'normal');
    doc.text(formatBRL(estatisticas.receitaTotal), col1X + 40, col1Y);
    col1Y += 5;

    // Coluna 2
    doc.setFont('helvetica', 'bold');
    doc.text('Lucro Total:', col2X, col2Y);
    doc.setFont('helvetica', 'normal');
    doc.text(formatBRL(estatisticas.lucroTotal), col2X + 40, col2Y);
    col2Y += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Taxa Ocupação Média:', col2X, col2Y);
    doc.setFont('helvetica', 'normal');
    doc.text(`${estatisticas.taxaOcupacaoMedia.toFixed(1)}%`, col2X + 40, col2Y);

    yPos = Math.max(col1Y, col2Y) + 8;

    // ===== DESTAQUES =====
    doc.setFillColor(232, 245, 233);
    doc.rect(15, yPos - 3, (pageWidth - 35) / 2, 25, 'F');
    
    doc.setFillColor(255, 235, 238);
    doc.rect(pageWidth / 2 + 5, yPos - 3, (pageWidth - 35) / 2, 25, 'F');

    // Mais Rentável
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(67, 160, 71);
    doc.text('Veiculo Mais Rentavel', 20, yPos);
    
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text(veiculoMaisLucro?.nomeCompleto || 'N/A', 20, yPos + 5);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Receita: ${formatBRL(veiculoMaisLucro?.receitaLiquida || 0)}`, 20, yPos + 10);
    doc.text(`Alugueis: ${veiculoMaisLucro?.totalAlugueis || 0}`, 20, yPos + 14);
    doc.text(`Ocupacao: ${(veiculoMaisLucro?.taxaOcupacao || 0).toFixed(1)}%`, 20, yPos + 18);

    // Menos Rentável
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(211, 47, 47);
    doc.text('Veiculo Menos Rentavel', pageWidth / 2 + 10, yPos);
    
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text(veiculoMenosLucro?.nomeCompleto || 'N/A', pageWidth / 2 + 10, yPos + 5);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Receita: ${formatBRL(veiculoMenosLucro?.receitaLiquida || 0)}`, pageWidth / 2 + 10, yPos + 10);
    doc.text(`Alugueis: ${veiculoMenosLucro?.totalAlugueis || 0}`, pageWidth / 2 + 10, yPos + 14);
    doc.text(`Ocupacao: ${(veiculoMenosLucro?.taxaOcupacao || 0).toFixed(1)}%`, pageWidth / 2 + 10, yPos + 18);

    yPos += 30;

    // ===== TABELA =====
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(247, 139, 0);
    doc.text('ANÁLISE COMPLETA - TODOS OS VEÍCULOS:', 15, yPos);
    yPos += 2;

    const tableColumns = ['Veículo', 'Aluguéis', 'Dias', 'Ocupação', 'Receita Bruta', 'Receita Líquida', 'Lucro', 'ROI', 'Ticket'];
    const tableData = dados.map(v => [
      v.nomeVeiculo,
      v.totalAlugueis || 0,
      v.totalDiasAlugados || 0,
      `${(v.taxaOcupacao || 0).toFixed(1)}%`,
      formatBRL(v.receitaBruta || 0),
      formatBRL(v.receitaLiquida || 0),
      formatBRL(v.lucroTotal || 0),
      `${(v.roi || 0).toFixed(1)}%`,
      formatBRL(v.ticketMedio || 0)
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [tableColumns],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [247, 139, 0],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 7,
        cellPadding: 1.5
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { left: 15, right: 15 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { halign: 'center' },
        2: { halign: 'center' },
        3: { halign: 'center' },
      }
    });

    // Rodapé
    const finalY = doc.lastAutoTable.finalY || yPos + 50;
    if (finalY < pageHeight - 15) {
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'italic');
      doc.text(
        `Relatorio gerado automaticamente pelo sistema Delta Rent Car - ${dataAtual}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    const dataArquivo = new Date().toISOString().split('T')[0];
    doc.save(`analise-veiculos-${dataArquivo}.pdf`);
  };

  const exportarExcel = () => {
    const dataAtual = new Date().toLocaleString('pt-BR');

    // ===== CABEÇALHO =====
    const cabecalho = [
      ['DELTA RENT CAR'],
      ['ANÁLISE DETALHADA DE VEÍCULOS'],
      [`Gerado em: ${dataAtual}`],
      [''],
    ];

    // ===== RESUMO ESTATÍSTICO =====
    const resumo = [
      ['RESUMO ESTATÍSTICO'],
      ['Total de Veículos:', estatisticas.totalVeiculos],
      ['Receita Total:', formatBRL(estatisticas.receitaTotal)],
      ['Lucro Total:', formatBRL(estatisticas.lucroTotal)],
      ['Taxa Ocupação Média:', `${estatisticas.taxaOcupacaoMedia.toFixed(1)}%`],
      [''],
    ];

    // ===== DESTAQUES =====
    const destaques = [
      ['DESTAQUES'],
      ['Veículo Mais Rentável:', veiculoMaisLucro?.nomeCompleto || 'N/A'],
      ['  Receita Líquida:', formatBRL(veiculoMaisLucro?.receitaLiquida || 0)],
      ['  Aluguéis:', veiculoMaisLucro?.totalAlugueis || 0],
      ['  Ocupação:', `${(veiculoMaisLucro?.taxaOcupacao || 0).toFixed(1)}%`],
      [''],
      ['Veículo Menos Rentável:', veiculoMenosLucro?.nomeCompleto || 'N/A'],
      ['  Receita Líquida:', formatBRL(veiculoMenosLucro?.receitaLiquida || 0)],
      ['  Aluguéis:', veiculoMenosLucro?.totalAlugueis || 0],
      ['  Ocupação:', `${(veiculoMenosLucro?.taxaOcupacao || 0).toFixed(1)}%`],
      [''],
    ];

    // ===== DADOS DA TABELA =====
    const dadosHeader = ['Veículo', 'Aluguéis', 'Dias Alugados', 'Taxa Ocupação', 'Receita Bruta', 'Receita Líquida', 'Lucro Total', 'ROI', 'Ticket Médio'];
    const dadosBody = dados.map(v => [
      v.nomeCompleto,
      v.totalAlugueis || 0,
      v.totalDiasAlugados || 0,
      `${(v.taxaOcupacao || 0).toFixed(1)}%`,
      formatBRL(v.receitaBruta || 0),
      formatBRL(v.receitaLiquida || 0),
      formatBRL(v.lucroTotal || 0),
      `${(v.roi || 0).toFixed(1)}%`,
      formatBRL(v.ticketMedio || 0)
    ]);

    const tabelaDados = [
      ['ANÁLISE COMPLETA:'],
      dadosHeader,
      ...dadosBody
    ];

    // ===== MONTAGEM FINAL =====
    const dadosCompletos = [
      ...cabecalho,
      ...resumo,
      ...destaques,
      ...tabelaDados
    ];

    const ws = XLSX.utils.aoa_to_sheet(dadosCompletos);

    // Larguras das colunas
    const colWidths = dadosHeader.map((_, idx) => {
      const maxLength = Math.max(
        dadosHeader[idx]?.length || 10,
        ...dadosBody.map(row => (row[idx]?.toString() || '').length)
      );
      return { wch: Math.min(maxLength + 2, 40) };
    });
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Análise Veículos');

    const dataArquivo = new Date().toISOString().split('T')[0];
    const nomeArquivo = `analise-veiculos-${dataArquivo}.xlsx`;
    const buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([buffer]), nomeArquivo);
  };

  // Colunas para o ranking com filtro
  const columns = [
    { 
      accessorKey: 'nomeCompleto', 
      header: 'Veículo',
      size: 280,
    },
    { 
      accessorKey: 'totalAlugueis', 
      header: 'Aluguéis',
      size: 100,
      Cell: ({ cell }) => (
        <Chip label={cell.getValue() || 0} color="primary" size="small" />
      )
    },
    { 
      accessorKey: 'totalDiasAlugados', 
      header: 'Dias Alugados',
      size: 120,
      Cell: ({ cell }) => cell.getValue() || 0
    },
    { 
      accessorKey: 'taxaOcupacao', 
      header: 'Taxa Ocupação',
      size: 130,
      Cell: ({ cell }) => {
        const valor = cell.getValue() || 0;
        return (
          <Chip 
            label={`${valor.toFixed(1)}%`} 
            color={valor > 70 ? 'success' : valor > 40 ? 'warning' : 'error'}
            size="small"
          />
        );
      }
    },
    { 
      accessorKey: 'receitaBruta', 
      header: 'Receita Bruta',
      size: 140,
      Cell: ({ cell }) => formatBRL(cell.getValue() || 0)
    },
    { 
      accessorKey: 'receitaLiquida', 
      header: 'Receita Líquida',
      size: 140,
      Cell: ({ cell }) => (
        <Typography fontWeight="bold" color="success.main">
          {formatBRL(cell.getValue() || 0)}
        </Typography>
      )
    },
    { 
      accessorKey: 'lucroTotal', 
      header: 'Lucro Total',
      size: 130,
      Cell: ({ cell }) => {
        const valor = cell.getValue() || 0;
        return (
          <Typography 
            fontWeight="bold" 
            color={valor >= 0 ? 'success.main' : 'error.main'}
          >
            {formatBRL(valor)}
          </Typography>
        );
      }
    },
    { 
      accessorKey: 'roi', 
      header: 'ROI',
      size: 100,
      Cell: ({ cell }) => {
        const valor = cell.getValue() || 0;
        return (
          <Chip 
            icon={valor >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
            label={`${valor.toFixed(1)}%`} 
            color={valor > 50 ? 'success' : valor > 0 ? 'warning' : 'error'}
            size="small"
          />
        );
      }
    },
    { 
      accessorKey: 'ticketMedio', 
      header: 'Ticket Médio',
      size: 130,
      Cell: ({ cell }) => formatBRL(cell.getValue() || 0)
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ px: 3, pt: 'calc(64px + 24px)', pb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Análise Detalhada de Veículos
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<PdfIcon />}
            onClick={exportarPDF}
            disabled={loading || dados.length === 0}
          >
            Exportar PDF
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<ExcelIcon />}
            onClick={exportarExcel}
            disabled={loading || dados.length === 0}
          >
            Exportar Excel
          </Button>
        </Stack>
      </Box>

      {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Cards de Estatísticas */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <CarIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">Total de Veículos</Typography>
                        <Typography variant="h5" fontWeight="bold">{estatisticas.totalVeiculos}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <MoneyIcon sx={{ fontSize: 40, color: '#43a047' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">Receita Total</Typography>
                        <Typography variant="h5" fontWeight="bold" color="success.main">
                          {formatBRL(estatisticas.receitaTotal)}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <TrendingUpIcon sx={{ fontSize: 40, color: '#ff9800' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">Lucro Total</Typography>
                        <Typography variant="h5" fontWeight="bold" color={estatisticas.lucroTotal >= 0 ? 'success.main' : 'error.main'}>
                          {formatBRL(estatisticas.lucroTotal)}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <ScheduleIcon sx={{ fontSize: 40, color: '#9c27b0' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">Taxa Ocupação Média</Typography>
                        <Typography variant="h5" fontWeight="bold">{estatisticas.taxaOcupacaoMedia.toFixed(1)}%</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Gráfico de Performance */}
            <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Receita Líquida por Veículo (Top 10)
              </Typography>
              <BarChart
                xAxis={[{ scaleType: 'band', data: maisAlugados.slice(0, 10).map(v => v.nomeVeiculo) }]}
                series={[{ 
                  data: maisAlugados.slice(0, 10).map(v => parseFloat(v.receitaLiquida || 0)), 
                  label: 'Receita Líquida', 
                  color: '#43a047' 
                }]}
                height={400}
              />
            </Paper>

            <Divider sx={{ my: 4 }} />

            {/* Destaques */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>🏆 Veículo Mais Rentável</Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {veiculoMaisLucro?.nomeCompleto || 'N/A'}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">Receita Líquida: <strong>{formatBRL(veiculoMaisLucro?.receitaLiquida || 0)}</strong></Typography>
                  <Typography variant="body2">Aluguéis: <strong>{veiculoMaisLucro?.totalAlugueis || 0}</strong></Typography>
                  <Typography variant="body2">Ocupação: <strong>{(veiculoMaisLucro?.taxaOcupacao || 0).toFixed(1)}%</strong></Typography>
                  <Typography variant="body2">ROI: <strong>{(veiculoMaisLucro?.roi || 0).toFixed(1)}%</strong></Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, bgcolor: '#ffebee', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>⚠️ Veículo Menos Rentável</Typography>
                  <Typography variant="h6" fontWeight="bold" color="error.main">
                    {veiculoMenosLucro?.nomeCompleto || 'N/A'}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">Receita Líquida: <strong>{formatBRL(veiculoMenosLucro?.receitaLiquida || 0)}</strong></Typography>
                  <Typography variant="body2">Aluguéis: <strong>{veiculoMenosLucro?.totalAlugueis || 0}</strong></Typography>
                  <Typography variant="body2">Ocupação: <strong>{(veiculoMenosLucro?.taxaOcupacao || 0).toFixed(1)}%</strong></Typography>
                  <Typography variant="body2">ROI: <strong>{(veiculoMenosLucro?.roi || 0).toFixed(1)}%</strong></Typography>
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Tabela Detalhada */}
            <Paper elevation={2} sx={{ p: 0, borderRadius: 2 }}>
              <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="h6" fontWeight="bold">
                  Análise Completa - Todos os Veículos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Dados detalhados de performance, rentabilidade e ocupação
                </Typography>
              </Box>
              <MaterialReactTable
                columns={columns}
                data={dados}
                enableColumnFilterModes
                enableColumnFilters
                enablePagination
                enableSorting
                enableStickyHeader
                enableFullScreenToggle
                enableDensityToggle
                enableHiding
                enableColumnResizing
                muiTableContainerProps={{ sx: { maxHeight: 500 } }}
                initialState={{
                  density: 'compact',
                  pagination: { pageSize: 20, pageIndex: 0 },
                  sorting: [{ id: 'receitaLiquida', desc: true }],
                }}
                muiTableBodyCellProps={{
                  sx: { fontSize: '0.875rem' }
                }}
                muiTableHeadCellProps={{
                  sx: { 
                    bgcolor: '#f5f5f5',
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                  }
                }}
              />
            </Paper>
          </>
        )}
    </Container>
  );
}